import parser from 'cron-parser'
import cronstrue from 'cronstrue'
import moment from 'moment'
import forge from 'node-forge'

/**
 * Generates an RSA key pair and converts them to appropriate formats.
 *
 * This function generates a 2048-bit RSA key pair using the Forge library.
 * The public key is converted to OpenSSH format, and the private key is
 * converted to PEM format.
 *
 * @returns {Object} An object containing the following properties:
 * - {string} sshPublicKey - The public key in OpenSSH format.
 * - {string} pemPrivateKey - The private key in PEM format.
 */
export function generateSSHKeyPair() {
  // Generate RSA key pair
  const { privateKey, publicKey } = forge.pki.rsa.generateKeyPair(2048)

  // Convert public key to OpenSSH format
  const sshPublicKey = forge.ssh.publicKeyToOpenSSH(publicKey)

  // Convert private key to PEM format
  const pemPrivateKey = forge.pki.privateKeyToPem(privateKey)

  return { sshPublicKey, pemPrivateKey }
}

export const getImage = base64 => {
  const array = []
  const blobBin = atob(base64.split(',')[1])
  for (let i = 0; i < blobBin.length; i++) {
    array.push(blobBin.charCodeAt(i))
  }
  const image = new Blob([new Uint8Array(array)], { type: 'image/png' })
  return image
}

export const doubleEncode = name => {
  if (typeof name === 'string' || name instanceof String) {
    const reducer = (str, cur) => `${str}/${encodeURI(encodeURIComponent(cur))}`
    return name.split('/').reduce(reducer, '').substring(1)
  }
  return name
}

export const sanitizeRefName = name => {
  if (typeof name === 'string' || name instanceof String) {
    if (name.includes('/')) {
      return name.split('/').join('%2F')
    }
  }
  return name
}

export const deepGet = (obj = {}, path = '') => {
  if (!obj || (Object.keys(obj).length === 0 && obj.constructor === Object) || !path) {
    return undefined
  }
  const paths = path.split('.')
  let current = obj
  let i
  for (i = 0; i < paths.length; ++i) {
    // check commit 989eef840, "==" is not equal to "==="
    if (current[paths[i]] === undefined || current[paths[i]] === null) {
      return undefined
    }
    current = current[paths[i]]
  }
  return current
}

export const constructDeepObject = (path = '') => {
  if (!path) {
    return undefined
  }

  const paths = path.split('.')

  const obj = {}
  let current = null

  for (let i = 0; i < paths.length; ++i) {
    if (current === null) {
      obj[paths[i]] = {}
      current = obj[paths[i]]
    } else {
      current[paths[i]] = i === paths.length - 1 ? undefined : {}
      current = current[paths[i]]
    }
  }

  return obj
}

export const deepSet = (obj = {}, path = '', value = undefined) => {
  if ((Object.keys(obj).length === 0 && obj.constructor === Object) || !path) {
    return undefined
  }

  const paths = path.split('.')
  let current = obj

  for (let i = 0; i < paths.length; ++i) {
    if (!current[paths[i]]) current[paths[i]] = {}

    if (i === paths.length - 1) current[paths[i]] = value

    current = current[paths[i]]
  }

  return obj
}

export const findCacheReference = (existing, { args: { id }, toReference }, __typename) => {
  if (existing || !id) return existing

  return toReference({
    __typename,
    id
  })
}

export const dedupeNodes = nodes => {
  const nodeRefs = []
  const newNodes = []

  if (!nodes?.[0]?.__ref) return nodes

  nodes.forEach(node => {
    if (nodeRefs.indexOf(node?.__ref) === -1) {
      newNodes.push(node)
      nodeRefs.push(node?.__ref)
    }
  })

  return newNodes
}

export const connectionTypeMerge = (existing = {}, incoming = {}, data) => {
  const { update, truncate, after } = data.variables
  const { pageInfo: existingPageInfo } = existing
  const { pageInfo: incomingPageInfo } = incoming
  const taskConnection = {}

  if (
    incoming.storyPoints != null ||
    incoming.completedStoryPoints != null ||
    incoming.estimatedTime != null ||
    incoming.actualTime != null
  ) {
    taskConnection.storyPoints = incoming.storyPoints || 0
    taskConnection.completedStoryPoints = incoming.completedStoryPoints || 0
    taskConnection.estimatedTime = incoming.estimatedTime || 0
    taskConnection.actualTime = incoming.actualTime || 0
  }

  if (data.variables.subscriptionIds) {
    const existingStartCursor = parseInt(atob(existingPageInfo.startCursor))

    const incomingNodeIndexes = incoming.nodes.map(node => node?.__ref)

    const newNodes = existing.nodes.filter(node => incomingNodeIndexes.indexOf(node?.__ref) === -1)

    const extraProps = {}

    if (typeof incoming.totalCount !== 'undefined') extraProps.totalCount = incoming.totalCount
    return {
      ...existing,
      ...extraProps,
      ...taskConnection,
      pageInfo: {
        ...existingPageInfo,
        endCursor: btoa(newNodes.length - (existingStartCursor - 1))
      },
      nodes: newNodes
    }
  }

  if (!incomingPageInfo) {
    return incoming
  }

  if (!existingPageInfo || !existingPageInfo.startCursor || !existingPageInfo.endCursor) {
    return incoming
  }

  if (incomingPageInfo.startCursor === null && incomingPageInfo.endCursor === null && incomingPageInfo.hasNextPage === false) {
    if (!truncate) {
      return update && update === true ? { ...existing, totalCount: incoming.totalCount, ...taskConnection } : { ...incoming }
    }

    let newNodes = [...existing.nodes]

    if (after) newNodes.splice(parseInt(atob(after)))
    else newNodes = []

    return {
      ...existing,
      totalCount: incoming.totalCount,
      ...taskConnection,
      pageInfo: {
        ...existingPageInfo,
        endCursor: btoa(newNodes.length),
        hasNextPage: incomingPageInfo.hasNextPage !== null ? incomingPageInfo.hasNextPage : true
      },
      nodes: newNodes
    }
  }

  if (existingPageInfo) {
    const existingStartCursor = parseInt(atob(existingPageInfo.startCursor))
    const existingEndCursor = parseInt(atob(existingPageInfo.endCursor))

    const incomingStartCursor = parseInt(atob(incomingPageInfo.startCursor))
    const incomingEndCursor = parseInt(atob(incomingPageInfo.endCursor))

    // There is overlap with existing and incoming data, merge existing and new data
    if (
      (incomingStartCursor >= existingStartCursor && incomingStartCursor <= existingEndCursor) ||
      (incomingEndCursor >= existingStartCursor && incomingEndCursor <= existingEndCursor)
    ) {
      if (!update || update === false) return incoming

      const incomingNodeIndexes = incoming.nodes.map(node => node?.__ref)

      let newNodes = existing.nodes.filter(node => incomingNodeIndexes.indexOf(node?.__ref) < 0)

      const bottomNodes = newNodes.splice(incomingStartCursor - 1)
      newNodes = [...newNodes, ...incoming.nodes, ...(truncate ? [] : bottomNodes)]

      const hasNextPage = (() => {
        if (truncate && bottomNodes.length > 0) return true

        return truncate ? incomingPageInfo.hasNextPage : existingPageInfo.hasNextPage
      })()

      return {
        ...existing,
        totalCount: incoming.totalCount,
        ...taskConnection,
        pageInfo: {
          ...existingPageInfo,
          endCursor: btoa(newNodes.length - (existingStartCursor - 1)),
          hasNextPage
        },
        nodes: newNodes
      }
    }

    // Incoming data is not next to existing data, return existing
    if (incomingStartCursor - existingEndCursor > 1 || existingStartCursor - incomingEndCursor > 1) {
      const incomingNodeIndexes = incoming.nodes.map(node => node.__ref)
      const newNodes = existing.nodes.filter(node => incomingNodeIndexes.indexOf(node.__ref) < 0)

      return {
        ...existing,
        totalCount: incoming.totalCount,
        ...taskConnection,
        pageInfo: {
          ...(existingPageInfo || incomingPageInfo),
          endCursor: btoa(newNodes.length),
          hasNextPage: true
        },
        nodes: newNodes
      }
    }
  }

  const dedupedNodes = dedupeNodes([...(existing.nodes || []), ...incoming.nodes])
  // Append results

  return {
    ...incoming,
    totalCount: incoming.totalCount,
    ...taskConnection,
    pageInfo: {
      ...(existingPageInfo || incomingPageInfo),
      endCursor: btoa(dedupedNodes.length),
      hasNextPage: incomingPageInfo.hasNextPage
    },
    nodes: dedupedNodes
  }
}

export const connectionTypeLogsMerge = (existing = {}, incoming = {}, data) => {
  const { update, truncate, after } = data.variables
  const { pageInfo: existingPageInfo } = existing
  const { pageInfo: incomingPageInfo } = incoming

  const typename = existing && existing.__typename ? existing.__typename : incoming.__typename

  if (!update) {
    return incoming
  }

  if (!incomingPageInfo) {
    return incoming
  }

  if (!existingPageInfo || !existingPageInfo.startCursor || !existingPageInfo.endCursor) {
    return incoming
  }

  if (!incomingPageInfo.startCursor || !incomingPageInfo.endCursor) {
    return existing
  }

  if (typename === 'LogsEdgeConnection') {
    const existingStartCursor = existingPageInfo.startCursor
    const existingEndCursorDate = moment.utc(existingStartCursor.createdAt)

    const incomingEndCursor = incomingPageInfo.endCursor
    const incomingEndCursorDate = moment.utc(incomingEndCursor.createdAt)

    if (
      incomingEndCursorDate < existingEndCursorDate ||
      (incomingEndCursorDate.diff(existingEndCursorDate) === 0 && incomingEndCursor.hashedLog < existingStartCursor.hashedLog)
    ) {
      return {
        ...incoming,
        totalCount: incoming.totalCount,
        pageInfo: {
          ...(existingPageInfo || incomingPageInfo),
          startCursor: incomingPageInfo.startCursor,
          hasPreviousPage: incomingPageInfo.hasPreviousPage
        },
        nodes: [...incoming.nodes, ...(existing.nodes || [])]
      }
    }
  } else {
    const existingStartCursor = parseInt(atob(existingPageInfo.startCursor))
    const existingEndCursor = parseInt(atob(existingPageInfo.endCursor))

    const incomingStartCursor = parseInt(atob(incomingPageInfo.startCursor))
    const incomingEndCursor = parseInt(atob(incomingPageInfo.endCursor))

    if (
      (incomingStartCursor >= existingStartCursor && incomingStartCursor <= existingEndCursor) ||
      (incomingEndCursor >= existingStartCursor && incomingEndCursor <= existingEndCursor)
    ) {
      return incoming
    }

    if (incomingEndCursor + 1 === existingStartCursor) {
      // Prepend results
      return {
        ...incoming,
        totalCount: incoming.totalCount,
        pageInfo: {
          ...(existingPageInfo || incomingPageInfo),
          startCursor: incomingPageInfo.startCursor,
          hasPreviousPage: incomingPageInfo.hasPreviousPage
        },
        nodes: [...incoming.nodes, ...(existing.nodes || [])]
      }
    }
  }

  // Append results
  return {
    ...incoming,
    totalCount: incoming.totalCount,
    pageInfo: {
      ...(existingPageInfo || incomingPageInfo),
      endCursor: incomingPageInfo.endCursor,
      hasNextPage: incomingPageInfo.hasNextPage
    },
    nodes: [...(existing.nodes || []), ...incoming.nodes]
  }
}

export const collectionTypeMerge = (existing = {}, incoming = {}, data) => {
  const { metaData: existingMetaData, collection: existingCollection } = existing
  const { metaData: incomingMetaData, collection: incomingCollection } = incoming

  return {
    ...existing,
    metaData: {
      ...existingMetaData,
      ...incomingMetaData
    },
    collection: incomingCollection || existingCollection
  }
}

export const mergeCollaborationType = (existing = {}, incoming = {}, data) => {
  if (!existing.nodes) return incoming

  const { pageInfo: existingPageInfo } = existing
  const { pageInfo: incomingPageInfo } = incoming

  if (!incomingPageInfo) return incoming

  if (!existingPageInfo || !existingPageInfo.startCursor || !existingPageInfo.endCursor) return incoming

  if (existingPageInfo) {
    const existingStartCursor = parseInt(atob(existingPageInfo.startCursor))
    const existingEndCursor = parseInt(atob(existingPageInfo.endCursor))

    const incomingStartCursor = parseInt(atob(incomingPageInfo.startCursor))
    const incomingEndCursor = parseInt(atob(incomingPageInfo.endCursor))

    // There is overlap with existing and incoming data, don't return existing data
    if (
      (incomingStartCursor >= existingStartCursor && incomingStartCursor <= existingEndCursor) ||
      (incomingEndCursor >= existingStartCursor && incomingEndCursor <= existingEndCursor)
    ) {
      return incoming
    }
  }

  const newNodes = []

  incoming.nodes.forEach(contribution => {
    const existingContribution = existing.nodes.find(
      existingContributionItem => existingContributionItem.authorEmail === contribution.authorEmail
    )
    let newContribution = {}
    if (existingContribution) {
      newContribution = {
        ...existingContribution,
        commitCount: existingContribution.commitCount + contribution.commitCount,
        coordinates: [...existingContribution.coordinates, ...contribution.coordinates]
      }
    } else {
      newContribution = contribution
    }
    newNodes.push(newContribution)
  })

  existing.nodes.forEach(contribution => {
    const incomingContribution = incoming.nodes.find(
      incomingContributionItem => incomingContributionItem.authorEmail === contribution.authorEmail
    )
    if (!incomingContribution) {
      newNodes.push(contribution)
    }
  })

  // Append results
  return {
    ...incoming,
    pageInfo: {
      ...(existingPageInfo || incomingPageInfo),
      endCursor: incomingPageInfo.endCursor,
      hasNextPage: incomingPageInfo.hasNextPage
    },
    nodes: [...newNodes]
  }
}

// Used in cacheTypePolicies for Document types
export function findTabIdForSection(existing, { toReference, readField }) {
  /** @type {string|null} */
  const itemType = readField('itemType')
  if (itemType === 'tab') {
    // This section is a tab, return itself
    return readField('id') || null
  }

  /** Find the root tabId. If there is no root tab, return null
   * @param {string|null} groupId
   * @param {string|null} tabId
   * @returns {string|null}
   */
  const findTabId = (groupId, tabId = null) => {
    if (groupId == null) {
      return tabId
    }
    // if groupId != null, check if the parent is a tab
    const parentSection = toReference({
      __typename: 'Section',
      id: groupId
    })
    if (readField('itemType', parentSection) === 'tab') {
      // parent is a tab, return groupId
      return groupId
    }
    // parent was not a tab, go further up the rabbit hole
    return findTabId(readField('groupId', parentSection), tabId)
  }

  return findTabId(readField('groupId'), null)
}

// Used in cacheTypePolicies for Document types
export function findDepthForSection(existing, { toReference, readField }) {
  /** @type {string|undefined} */
  const groupId = readField('groupId')
  if (groupId == null) {
    return 0
  }
  const parentSection = toReference({
    __typename: 'Section',
    id: groupId
  })
  /**
   * The itemType of the parent
   * @type {string}
   */
  if (readField('itemType', parentSection) === 'tab') {
    // parent is a tab, return depth of 0
    return 0
  }
  /** Sum up the number of groups the section/group is nested within
   * @param {string|undefined} groupId
   * @param {number} value
   * @returns {number}
   */
  const reduceDepthValue = (groupId, value = 0) => {
    if (groupId == null) {
      return value
    }
    // if groupId != null, check if the parent is a tab
    const parentSection = toReference({
      __typename: 'Section',
      id: groupId
    })
    if (readField('itemType', parentSection) === 'tab') {
      // parent is a tab, return value
      return value
    }
    let valueToUpdate = value
    valueToUpdate += 1
    return reduceDepthValue(readField('groupId', parentSection), valueToUpdate)
  }

  return reduceDepthValue(groupId, 0)
}

export function findDepthForOtelTrace(existing, { toReference, readField }) {
  /** @type {string|undefined} */
  const parentSpanId = readField('parentSpanId')
  if (parentSpanId == null || parentSpanId === '') {
    return 0
  }

  const reduceDepthValue = (parentSpanId, value = 0) => {
    if (parentSpanId == null || parentSpanId === '') {
      return value
    }

    const parent = toReference({
      __typename: 'OtelTrace',
      id: parentSpanId
    })

    let valueToUpdate = value
    valueToUpdate += 1

    return reduceDepthValue(readField('parentSpanId', parent), valueToUpdate)
  }

  return reduceDepthValue(parentSpanId, 0)
}

export function b64EncodeUnicode(str) {
  return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => String.fromCharCode(`0x${p1}`)))
}

export function b64DecodeUnicode(str) {
  try {
    return decodeURIComponent(
      atob(str)
        .split('')
        .map(c => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    )
  } catch (err) {
    try {
      return decodeURIComponent(atob(str))
    } catch (err) {
      return str
    }
  }
}

export const graphQLErrorFinder = (baseKey, errorKey, graphQLErrors) => {
  let error = null

  if (Array.isArray(graphQLErrors) && graphQLErrors.length > 0) {
    for (let i = 0; i < graphQLErrors.length; i++) {
      const baseErrors = deepGet(graphQLErrors[i], `extensions.${baseKey}`)
      if (!baseErrors) continue

      error = baseErrors.find(base => base.error === errorKey)

      if (error) break
    }
  }

  return error
}

export const isJsonObject = obj => {
  try {
    const str = JSON.stringify(obj)
    JSON.parse(str)
  } catch (e) {
    return false
  }

  return true
}

export const isBlank = obj => {
  if (obj === null || obj === undefined) return true
  if (Array.isArray(obj) || typeof obj === 'string') return !obj.length
  if (typeof obj === 'number') return false

  if (isJsonObject(obj)) return !Object.keys(obj).length

  return false
}

export const isPresent = obj => !isBlank(obj)

export const validateCronParser = cronSyntax => {
  let parsedDate = ''
  try {
    parsedDate = parser.parseExpression(cronSyntax).next().toString()
  } catch (err) {
    parsedDate = 'Invalid'
  }
  return parsedDate
}

export const validateCronSyntax = cronSyntax => {
  let cron = ''
  try {
    cron = cronstrue.toString(cronSyntax)
  } catch (err) {
    cron = 'Invalid'
  }
  return cron
}

export const codegiantMastheadLight = () =>
  "data:image/svg+xml,%3csvg width='551px' height='102px' viewBox='0 0 551 102' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3e%3cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3e%3cg id='codegiant-version' transform='translate(-151.000000%2c -284.000000)'%3e%3crect id='Rectangle' fill='%23884FFC' x='151' y='284' width='102' height='102' rx='10'%3e%3c/rect%3e%3cpath d='M209%2c296.999768 C209%2c300.313647 206.089965%2c303 202.500247%2c303 C198.910529%2c303 196%2c300.313647 196%2c296.999768 C196%2c293.686353 198.910529%2c291 202.500247%2c291 C206.089965%2c291 209%2c293.686353 209%2c296.999768' id='Fill-4' fill='white'%3e%3c/path%3e%3cpath d='M229%2c328.150126 C229%2c329.370373 228.289775%2c330.480979 227.176918%2c330.999229 L221.806285%2c333.500125 L227.176918%2c336.001021 C228.289775%2c336.518762 229%2c337.629369 229%2c338.850123 C229%2c341.154074 226.58881%2c342.676844 224.48828%2c341.698718 L215.596666%2c337.558307 C214.011688%2c336.820271 213%2c335.23862 213%2c333.500125 C213%2c331.761629 214.011688%2c330.179979 215.596666%2c329.441943 L224.48828%2c325.301024 C226.58881%2c324.323405 229%2c325.846175 229%2c328.150126 Z M210.986887%2c344.169047 C210.638444%2c346.390767 209.063118%2c351 202.000196%2c351 C194.937274%2c351 193.361948%2c346.390767 193.012999%2c344.169047 C192.916406%2c343.555829 193.370545%2c343 193.962746%2c343 L210.03714%2c343 C210.629341%2c343 211.083986%2c343.555829 210.986887%2c344.169047 Z M188%2c336.166089 C188%2c338.836029 185.761179%2c341 183%2c341 C180.238821%2c341 178%2c338.836029 178%2c336.166089 L178%2c330.833911 C178%2c328.163971 180.238821%2c326 183%2c326 C185.761179%2c326 188%2c328.163971 188%2c330.833911 L188%2c336.166089 Z M227.704998%2c305 L215.762197%2c305 C214.164752%2c305 212.648881%2c305.708676 211.618949%2c306.937283 L210.066045%2c308.790007 C209.036614%2c310.018614 207.520243%2c310.72729 205.922798%2c310.72729 L198.077202%2c310.72729 C196.479757%2c310.72729 194.963386%2c310.018614 193.933955%2c308.790007 L192.381051%2c306.937283 C191.351119%2c305.708676 189.834748%2c305 188.237303%2c305 L176.295002%2c305 C171.713859%2c305 168%2c308.734642 168%2c313.342546 L168%2c352.657454 C168%2c357.264855 171.713859%2c361 176.295002%2c361 L227.704998%2c361 C232.286141%2c361 236%2c357.264855 236%2c352.657454 L236%2c313.342546 C236%2c308.734642 232.286141%2c305 227.704998%2c305 L227.704998%2c305 Z' id='Fill-5' fill='white'%3e%3c/path%3e%3cpath d='M212.273014%2c368 L193.726986%2c368 C191.116055%2c368 189%2c370.014444 189%2c372.5 C189%2c374.985556 191.116055%2c377 193.726986%2c377 L212.273014%2c377 C214.883431%2c377 217%2c374.985556 217%2c372.5 C217%2c370.014444 214.883431%2c368 212.273014%2c368' id='Fill-6' fill='white'%3e%3c/path%3e%3cpath d='M272%2c335.955203 C272%2c321.868783 282.276756%2c312 296.430327%2c312 C304.453179%2c312 311.214048%2c315.139991 315%2c321.331217 L310.22297%2c324.560802 C306.977147%2c319.804762 301.929212%2c317.563227 296.430327%2c317.563227 C286.062708%2c317.563227 278.489963%2c324.920017 278.489963%2c335.955203 C278.489963%2c347.08082 286.062708%2c354.348016 296.430327%2c354.348016 C301.929212%2c354.348016 306.977147%2c352.1944 310.22297%2c347.439198 L315%2c350.669621 C311.214048%2c356.770414 304.453179%2c360 296.430327%2c360 C282.276756%2c360 272%2c350.041622 272%2c335.955203' id='Fill-7' fill='%23393740'%3e%3c/path%3e%3cpath d='M360%2c335.499579 C360%2c324.399832 352.570112%2c317 342.544693%2c317 C332.519273%2c317 325%2c324.399832 325%2c335.499579 C325%2c346.599326 332.519273%2c354 342.544693%2c354 C352.570112%2c354 360%2c346.599326 360%2c335.499579 Z M318%2c335.955203 C318%2c321.959215 328.227998%2c312 342.044797%2c312 C355.861596%2c312 366%2c321.959215 366%2c335.955203 C366%2c349.951191 355.861596%2c360 342.044797%2c360 C328.227998%2c360 318%2c349.951191 318%2c335.955203 L318%2c335.955203 Z' id='Fill-8' fill='%23393740'%3e%3c/path%3e%3cpath d='M413%2c335.499579 C413%2c324.399832 405.480547%2c317 395.545112%2c317 C385.519453%2c317 378%2c324.399832 378%2c335.499579 C378%2c346.599326 385.519453%2c354 395.545112%2c354 C405.480547%2c354 413%2c346.599326 413%2c335.499579 Z M419%2c293 L419%2c359.55133 L412.877079%2c359.55133 L412.877079%2c350.223848 C409.004132%2c356.592284 402.429872%2c360 394.594887%2c360 C381.085799%2c360 371%2c350.223848 371%2c335.962669 C371%2c321.70149 381.085799%2c312.014905 394.594887%2c312.014905 C402.160077%2c312.014905 408.643565%2c315.244325 412.606444%2c321.343224 L412.606444%2c293 L419%2c293 L419%2c293 Z' id='Fill-9' fill='%23393740'%3e%3c/path%3e%3cpath d='M435%2c333 L469%2c333 C468.179093%2c323.583116 461.343024%2c317 451.954063%2c317 C442.656976%2c317 435.820907%2c323.583116 435%2c333 Z M474.907475%2c337.928792 L434.516998%2c337.928792 C435.252055%2c347.798413 442.962451%2c354.348016 453.518839%2c354.348016 C459.39416%2c354.348016 464.626948%2c352.283995 468.297953%2c348.15679 L471.970671%2c352.283995 C467.65542%2c357.307981 460.955086%2c360 453.335502%2c360 C438.280527%2c360 428%2c349.951191 428%2c335.955203 C428%2c321.959215 438.005523%2c312 451.59124%2c312 C465.176956%2c312 475%2c321.780026 475%2c335.955203 C475%2c336.493607 474.907475%2c337.211199 474.907475%2c337.928792 L474.907475%2c337.928792 Z' id='Fill-10' fill='%23393740'%3e%3c/path%3e%3cpath d='M519%2c334.41131 C519%2c326.417572 512.932692%2c321 504.545384%2c321 C496.067308%2c321 490%2c326.417572 490%2c334.41131 C490%2c342.493737 496.067308%2c348 504.545384%2c348 C512.932692%2c348 519%2c342.493737 519%2c334.41131 Z M530%2c311.541759 L530%2c352.501908 C530%2c370.044082 521.027071%2c378 504.411274%2c378 C495.525413%2c378 486.729936%2c375.649848 481.310191%2c371.129287 L486.285477%2c362.629079 C490.461401%2c366.155573 497.214523%2c368.416275 503.789363%2c368.416275 C514.27312%2c368.416275 518.893503%2c363.533698 518.893503%2c353.767699 L518.893503%2c351.236117 C514.984586%2c355.576092 509.387389%2c357.656209 502.989171%2c357.656209 C489.395031%2c357.656209 479%2c348.252226 479%2c334.237389 C479%2c320.222552 489.395031%2c311 502.989171%2c311 C509.653567%2c311 515.516942%2c313.169566 519.427517%2c317.96185 L519.427517%2c311.541759 L530%2c311.541759 L530%2c311.541759 Z' id='Fill-11' fill='%23393740'%3e%3c/path%3e%3cpath d='M541%2c360 L552%2c360 L552%2c312 L541%2c312 L541%2c360 Z M540%2c297.000436 C540%2c293.080819 542.975561%2c290 547.000408%2c290 C551.025255%2c290 554%2c292.894419 554%2c296.719965 C554%2c300.826852 551.111811%2c304 547.000408%2c304 C542.975561%2c304 540%2c300.920923 540%2c297.000436 L540%2c297.000436 Z' id='Fill-12' fill='%23393740'%3e%3c/path%3e%3cpath d='M593%2c344.559117 L593%2c339 L581.569735%2c339 C574.03914%2c339 572%2c341.869087 572%2c345.364871 C572%2c349.400335 575.367971%2c352 581.038037%2c352 C586.441841%2c352 591.138644%2c349.489026 593%2c344.559117 Z M604%2c331.334039 L604%2c359.372974 L593.537946%2c359.372974 L593.537946%2c353.550828 C590.877752%2c357.671643 585.736088%2c360 578.643338%2c360 C567.827144%2c360 561%2c353.998106 561%2c345.667855 C561%2c337.694591 566.318735%2c331.42433 580.682131%2c331.42433 L592.9182%2c331.42433 L592.9182%2c330.707012 C592.9182%2c324.168384 589.016029%2c320.315936 581.126462%2c320.315936 C575.805246%2c320.315936 570.30944%2c322.107558 566.763893%2c325.063778 L562.419046%2c316.911602 C567.472175%2c312.969698 574.831358%2c311 582.455319%2c311 C596.108778%2c311 604%2c317.538628 604%2c331.334039 L604%2c331.334039 Z' id='Fill-13' fill='%23393740'%3e%3c/path%3e%3cpath d='M663%2c332.233616 L663%2c360 L651.594464%2c360 L651.594464%2c333.685154 C651.594464%2c325.064846 647.305465%2c320.890615 640.004116%2c320.890615 C631.882612%2c320.890615 626.407239%2c325.790615 626.407239%2c335.5 L626.407239%2c360 L615%2c360 L615%2c311.543692 L625.859617%2c311.543692 L625.859617%2c317.805461 C629.600994%2c313.358538 635.53286%2c311 642.6511%2c311 C654.331725%2c311 663%2c317.623384 663%2c332.233616' id='Fill-14' fill='%23393740'%3e%3c/path%3e%3cpath d='M702%2c356.77723 C699.320961%2c358.9263 695.304486%2c360 691.28551%2c360 C680.839341%2c360 674.857126%2c354.450185 674.857126%2c343.885315 L674.857126%2c320.875558 L667%2c320.875558 L667%2c311.922491 L674.857126%2c311.922491 L674.857126%2c301 L686.018261%2c301 L686.018261%2c311.922491 L698.785986%2c311.922491 L698.785986%2c320.875558 L686.018261%2c320.875558 L686.018261%2c343.616264 C686.018261%2c348.272026 688.339817%2c350.778717 692.625446%2c350.778717 C694.947003%2c350.778717 697.179396%2c350.152044 698.875149%2c348.809293 L702%2c356.77723' id='Fill-15' fill='%23393740'%3e%3c/path%3e%3c/g%3e%3c/g%3e%3c/svg%3e"

export const codegiantMastheadDark = () =>
  "data:image/svg+xml,%3csvg width='551px' height='102px' viewBox='0 0 551 102' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3e%3cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3e%3cg id='codegiant-version' transform='translate(-151.000000%2c -284.000000)'%3e%3crect id='Rectangle' fill='%23884FFC' x='151' y='284' width='102' height='102' rx='10'%3e%3c/rect%3e%3cpath d='M209%2c296.999768 C209%2c300.313647 206.089965%2c303 202.500247%2c303 C198.910529%2c303 196%2c300.313647 196%2c296.999768 C196%2c293.686353 198.910529%2c291 202.500247%2c291 C206.089965%2c291 209%2c293.686353 209%2c296.999768' id='Fill-4' fill='white'%3e%3c/path%3e%3cpath d='M229%2c328.150126 C229%2c329.370373 228.289775%2c330.480979 227.176918%2c330.999229 L221.806285%2c333.500125 L227.176918%2c336.001021 C228.289775%2c336.518762 229%2c337.629369 229%2c338.850123 C229%2c341.154074 226.58881%2c342.676844 224.48828%2c341.698718 L215.596666%2c337.558307 C214.011688%2c336.820271 213%2c335.23862 213%2c333.500125 C213%2c331.761629 214.011688%2c330.179979 215.596666%2c329.441943 L224.48828%2c325.301024 C226.58881%2c324.323405 229%2c325.846175 229%2c328.150126 Z M210.986887%2c344.169047 C210.638444%2c346.390767 209.063118%2c351 202.000196%2c351 C194.937274%2c351 193.361948%2c346.390767 193.012999%2c344.169047 C192.916406%2c343.555829 193.370545%2c343 193.962746%2c343 L210.03714%2c343 C210.629341%2c343 211.083986%2c343.555829 210.986887%2c344.169047 Z M188%2c336.166089 C188%2c338.836029 185.761179%2c341 183%2c341 C180.238821%2c341 178%2c338.836029 178%2c336.166089 L178%2c330.833911 C178%2c328.163971 180.238821%2c326 183%2c326 C185.761179%2c326 188%2c328.163971 188%2c330.833911 L188%2c336.166089 Z M227.704998%2c305 L215.762197%2c305 C214.164752%2c305 212.648881%2c305.708676 211.618949%2c306.937283 L210.066045%2c308.790007 C209.036614%2c310.018614 207.520243%2c310.72729 205.922798%2c310.72729 L198.077202%2c310.72729 C196.479757%2c310.72729 194.963386%2c310.018614 193.933955%2c308.790007 L192.381051%2c306.937283 C191.351119%2c305.708676 189.834748%2c305 188.237303%2c305 L176.295002%2c305 C171.713859%2c305 168%2c308.734642 168%2c313.342546 L168%2c352.657454 C168%2c357.264855 171.713859%2c361 176.295002%2c361 L227.704998%2c361 C232.286141%2c361 236%2c357.264855 236%2c352.657454 L236%2c313.342546 C236%2c308.734642 232.286141%2c305 227.704998%2c305 L227.704998%2c305 Z' id='Fill-5' fill='white'%3e%3c/path%3e%3cpath d='M212.273014%2c368 L193.726986%2c368 C191.116055%2c368 189%2c370.014444 189%2c372.5 C189%2c374.985556 191.116055%2c377 193.726986%2c377 L212.273014%2c377 C214.883431%2c377 217%2c374.985556 217%2c372.5 C217%2c370.014444 214.883431%2c368 212.273014%2c368' id='Fill-6' fill='white'%3e%3c/path%3e%3cpath d='M272%2c335.955203 C272%2c321.868783 282.276756%2c312 296.430327%2c312 C304.453179%2c312 311.214048%2c315.139991 315%2c321.331217 L310.22297%2c324.560802 C306.977147%2c319.804762 301.929212%2c317.563227 296.430327%2c317.563227 C286.062708%2c317.563227 278.489963%2c324.920017 278.489963%2c335.955203 C278.489963%2c347.08082 286.062708%2c354.348016 296.430327%2c354.348016 C301.929212%2c354.348016 306.977147%2c352.1944 310.22297%2c347.439198 L315%2c350.669621 C311.214048%2c356.770414 304.453179%2c360 296.430327%2c360 C282.276756%2c360 272%2c350.041622 272%2c335.955203' id='Fill-7' fill='%23f8f8f8'%3e%3c/path%3e%3cpath d='M360%2c335.499579 C360%2c324.399832 352.570112%2c317 342.544693%2c317 C332.519273%2c317 325%2c324.399832 325%2c335.499579 C325%2c346.599326 332.519273%2c354 342.544693%2c354 C352.570112%2c354 360%2c346.599326 360%2c335.499579 Z M318%2c335.955203 C318%2c321.959215 328.227998%2c312 342.044797%2c312 C355.861596%2c312 366%2c321.959215 366%2c335.955203 C366%2c349.951191 355.861596%2c360 342.044797%2c360 C328.227998%2c360 318%2c349.951191 318%2c335.955203 L318%2c335.955203 Z' id='Fill-8' fill='%23f8f8f8'%3e%3c/path%3e%3cpath d='M413%2c335.499579 C413%2c324.399832 405.480547%2c317 395.545112%2c317 C385.519453%2c317 378%2c324.399832 378%2c335.499579 C378%2c346.599326 385.519453%2c354 395.545112%2c354 C405.480547%2c354 413%2c346.599326 413%2c335.499579 Z M419%2c293 L419%2c359.55133 L412.877079%2c359.55133 L412.877079%2c350.223848 C409.004132%2c356.592284 402.429872%2c360 394.594887%2c360 C381.085799%2c360 371%2c350.223848 371%2c335.962669 C371%2c321.70149 381.085799%2c312.014905 394.594887%2c312.014905 C402.160077%2c312.014905 408.643565%2c315.244325 412.606444%2c321.343224 L412.606444%2c293 L419%2c293 L419%2c293 Z' id='Fill-9' fill='%23f8f8f8'%3e%3c/path%3e%3cpath d='M435%2c333 L469%2c333 C468.179093%2c323.583116 461.343024%2c317 451.954063%2c317 C442.656976%2c317 435.820907%2c323.583116 435%2c333 Z M474.907475%2c337.928792 L434.516998%2c337.928792 C435.252055%2c347.798413 442.962451%2c354.348016 453.518839%2c354.348016 C459.39416%2c354.348016 464.626948%2c352.283995 468.297953%2c348.15679 L471.970671%2c352.283995 C467.65542%2c357.307981 460.955086%2c360 453.335502%2c360 C438.280527%2c360 428%2c349.951191 428%2c335.955203 C428%2c321.959215 438.005523%2c312 451.59124%2c312 C465.176956%2c312 475%2c321.780026 475%2c335.955203 C475%2c336.493607 474.907475%2c337.211199 474.907475%2c337.928792 L474.907475%2c337.928792 Z' id='Fill-10' fill='%23f8f8f8'%3e%3c/path%3e%3cpath d='M519%2c334.41131 C519%2c326.417572 512.932692%2c321 504.545384%2c321 C496.067308%2c321 490%2c326.417572 490%2c334.41131 C490%2c342.493737 496.067308%2c348 504.545384%2c348 C512.932692%2c348 519%2c342.493737 519%2c334.41131 Z M530%2c311.541759 L530%2c352.501908 C530%2c370.044082 521.027071%2c378 504.411274%2c378 C495.525413%2c378 486.729936%2c375.649848 481.310191%2c371.129287 L486.285477%2c362.629079 C490.461401%2c366.155573 497.214523%2c368.416275 503.789363%2c368.416275 C514.27312%2c368.416275 518.893503%2c363.533698 518.893503%2c353.767699 L518.893503%2c351.236117 C514.984586%2c355.576092 509.387389%2c357.656209 502.989171%2c357.656209 C489.395031%2c357.656209 479%2c348.252226 479%2c334.237389 C479%2c320.222552 489.395031%2c311 502.989171%2c311 C509.653567%2c311 515.516942%2c313.169566 519.427517%2c317.96185 L519.427517%2c311.541759 L530%2c311.541759 L530%2c311.541759 Z' id='Fill-11' fill='%23f8f8f8'%3e%3c/path%3e%3cpath d='M541%2c360 L552%2c360 L552%2c312 L541%2c312 L541%2c360 Z M540%2c297.000436 C540%2c293.080819 542.975561%2c290 547.000408%2c290 C551.025255%2c290 554%2c292.894419 554%2c296.719965 C554%2c300.826852 551.111811%2c304 547.000408%2c304 C542.975561%2c304 540%2c300.920923 540%2c297.000436 L540%2c297.000436 Z' id='Fill-12' fill='%23f8f8f8'%3e%3c/path%3e%3cpath d='M593%2c344.559117 L593%2c339 L581.569735%2c339 C574.03914%2c339 572%2c341.869087 572%2c345.364871 C572%2c349.400335 575.367971%2c352 581.038037%2c352 C586.441841%2c352 591.138644%2c349.489026 593%2c344.559117 Z M604%2c331.334039 L604%2c359.372974 L593.537946%2c359.372974 L593.537946%2c353.550828 C590.877752%2c357.671643 585.736088%2c360 578.643338%2c360 C567.827144%2c360 561%2c353.998106 561%2c345.667855 C561%2c337.694591 566.318735%2c331.42433 580.682131%2c331.42433 L592.9182%2c331.42433 L592.9182%2c330.707012 C592.9182%2c324.168384 589.016029%2c320.315936 581.126462%2c320.315936 C575.805246%2c320.315936 570.30944%2c322.107558 566.763893%2c325.063778 L562.419046%2c316.911602 C567.472175%2c312.969698 574.831358%2c311 582.455319%2c311 C596.108778%2c311 604%2c317.538628 604%2c331.334039 L604%2c331.334039 Z' id='Fill-13' fill='%23f8f8f8'%3e%3c/path%3e%3cpath d='M663%2c332.233616 L663%2c360 L651.594464%2c360 L651.594464%2c333.685154 C651.594464%2c325.064846 647.305465%2c320.890615 640.004116%2c320.890615 C631.882612%2c320.890615 626.407239%2c325.790615 626.407239%2c335.5 L626.407239%2c360 L615%2c360 L615%2c311.543692 L625.859617%2c311.543692 L625.859617%2c317.805461 C629.600994%2c313.358538 635.53286%2c311 642.6511%2c311 C654.331725%2c311 663%2c317.623384 663%2c332.233616' id='Fill-14' fill='%23f8f8f8'%3e%3c/path%3e%3cpath d='M702%2c356.77723 C699.320961%2c358.9263 695.304486%2c360 691.28551%2c360 C680.839341%2c360 674.857126%2c354.450185 674.857126%2c343.885315 L674.857126%2c320.875558 L667%2c320.875558 L667%2c311.922491 L674.857126%2c311.922491 L674.857126%2c301 L686.018261%2c301 L686.018261%2c311.922491 L698.785986%2c311.922491 L698.785986%2c320.875558 L686.018261%2c320.875558 L686.018261%2c343.616264 C686.018261%2c348.272026 688.339817%2c350.778717 692.625446%2c350.778717 C694.947003%2c350.778717 697.179396%2c350.152044 698.875149%2c348.809293 L702%2c356.77723' id='Fill-15' fill='%23f8f8f8'%3e%3c/path%3e%3c/g%3e%3c/g%3e%3c/svg%3e"
